import { Link, NavLink, useNavigate } from "react-router-dom";
import { CalendarDays, LogOut, Menu, Shield, Swords, Trophy, X } from "lucide-react";
import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";

const links = [
  { to: "/", label: "Calendar", icon: CalendarDays, testid: "nav-calendar" },
  { to: "/leaderboard", label: "Leaderboards", icon: Trophy, testid: "nav-leaderboard" },
  { to: "/results", label: "Results", icon: Swords, testid: "nav-results" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const linkClass = ({ isActive }) =>
    `flex items-center gap-2 px-3 py-2 text-sm uppercase tracking-wider transition-colors duration-200 ${
      isActive ? "text-primary" : "text-neutral-400 hover:text-white"
    }`;

  return (
    <div className="grain min-h-screen flex flex-col">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-black/60 border-b border-white/10">
        <div className="mx-auto max-w-7xl px-5 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" data-testid="brand-logo" className="flex items-center group">
            <img
              src="https://customer-assets-39nsmqrw.emergentagent.net/job_tcg-calendar-pro/artifacts/b5f4n8cm_logo.webp"
              alt="Hex Labs Games"
              className="h-9 sm:h-10 w-auto group-hover:scale-105 transition-transform duration-300"
            />
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {links.map((l) => (
              <NavLink key={l.to} to={l.to} className={linkClass} data-testid={l.testid} end={l.to === "/"}>
                <l.icon className="h-4 w-4" />
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-2">
            {user && user.role === "admin" && (
              <NavLink to="/admin" className={linkClass} data-testid="nav-admin">
                <Shield className="h-4 w-4" />
                Admin
              </NavLink>
            )}
            {user ? (
              <Button
                data-testid="logout-btn"
                variant="ghost"
                size="sm"
                className="text-neutral-400 hover:text-white"
                onClick={async () => {
                  await logout();
                  navigate("/");
                }}
              >
                <LogOut className="h-4 w-4 mr-2" /> Sign out
              </Button>
            ) : (
              <Button
                data-testid="staff-login-btn"
                size="sm"
                className="bg-primary text-black font-bold uppercase tracking-wider rounded-full hover:bg-[#FFA94D] hover:scale-[1.03] transition-transform duration-300"
                onClick={() => navigate("/login")}
              >
                Staff Login
              </Button>
            )}
          </div>

          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden text-white p-2"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        {open && (
          <div className="md:hidden border-t border-white/10 bg-black/90 px-5 py-3" data-testid="mobile-menu">
            {links.map((l) => (
              <NavLink key={l.to} to={l.to} className={linkClass} onClick={() => setOpen(false)} end={l.to === "/"}>
                <l.icon className="h-4 w-4" /> {l.label}
              </NavLink>
            ))}
            {user && user.role === "admin" && (
              <NavLink to="/admin" className={linkClass} onClick={() => setOpen(false)}>
                <Shield className="h-4 w-4" /> Admin
              </NavLink>
            )}
            <NavLink to={user ? "/" : "/login"} className={linkClass} onClick={() => { setOpen(false); if (user) logout(); }}>
              <LogOut className="h-4 w-4" /> {user ? "Sign out" : "Staff Login"}
            </NavLink>
          </div>
        )}
      </header>

      <main className="flex-1 relative z-10">{children}</main>

      <footer className="border-t border-white/10 mt-16">
        <div className="mx-auto max-w-7xl px-5 lg:px-10 py-8 flex flex-col sm:flex-row gap-3 justify-between text-xs text-neutral-500">
          <p>© {new Date().getFullYear()} Hex Labs Games · 1a Edward Bright Close, Maldon CM9 5RU</p>
          <a
            href="https://hexlabsgames.com/collections/event-tickets"
            target="_blank"
            rel="noreferrer"
            className="hover:text-primary transition-colors duration-200"
            data-testid="footer-shop-link"
          >
            Shop event tickets →
          </a>
        </div>
      </footer>
    </div>
  );
}
